import { useCallback, useMemo, useRef, useState } from 'react';
import './ShowroomPage.css';
import { hasInteriorView } from '../data/carModels';
import { DRIVING_STUDIO_ID } from '../data/studioOptions';
import { drivingCourseOptions } from '../data/courseOptions';
import {
  drivingChallengeOptions,
  type DrivingChallengeId,
} from '../data/challengeOptions';
import { useCarConfiguration } from '../hooks/useCarConfiguration';
import { useColorTheme } from '../hooks/useColorTheme';
import { useDrivingInput } from '../hooks/useDrivingInput';
import { useDrivingPreferences } from '../hooks/useDrivingPreferences';
import { useDrivingRecords } from '../hooks/useDrivingRecords';
import { useGraphicsQuality } from '../hooks/useGraphicsQuality';
import { createDrivingSession, type DrivingSessionState } from '../model/drivingSession';
import {
  createDrivingChallengeState,
  type DrivingChallengeState,
} from '../model/drivingChallenges';
import {
  createDrivingTelemetryState,
  type DrivingTelemetryState,
} from '../model/drivingTelemetry';
import { createFrameStats, type FrameStatsValue } from '../three/FrameStats';
import { useGamepadHaptics } from '../hooks/useGamepadHaptics';
import { useStackedLayout, useTouchOnly } from '../hooks/useStackedLayout';
import { formatModelSubtitle } from '../model/modelLabels';
import {
  SPAWN_REASONS,
  STAGE_MODES,
  catalogPeakPowerKw,
  createVehicleState,
  respawnVehicle,
  type StageMode,
  type VehicleState,
} from '../model/vehicleDriving';
import { VIEW_MODES, type ViewMode } from '../three/cameraPoses';
import { ShowroomCanvas } from '../three/ShowroomCanvas';
import { buildClassName } from './buildClassName';
import { DrivingControls } from './DrivingControls';
import { DrivingHud } from './DrivingHud';
import { DrivingRunoffNotice } from './DrivingRunoffNotice';
import { OptionPanel } from './OptionPanel';
import { StageModeToggle } from './StageModeToggle';
import { ViewerErrorBoundary } from './ViewerErrorBoundary';
import { ViewerLoadingOverlay } from './ViewerLoadingOverlay';
import { ViewModeToggle } from './ViewModeToggle';

/**
 * 뷰어 조작 안내.
 *
 * 손가락으로만 조작하는 기기에서는 문구를 바꿉니다. "스크롤"은 마우스 휠을 가리키는
 * 말이고, 폰에서 그 말대로 하면(화면을 위로 쓸면) 확대가 아니라 아무 일도 일어나지
 * 않습니다 — 캔버스가 touch-action: none이라 스크롤 자체가 없습니다.
 */
function resolveViewerHint(viewMode: ViewMode, touchOnly: boolean): string {
  if (viewMode === VIEW_MODES.INTERIOR) {
    return '드래그하여 실내를 둘러보세요';
  }
  return touchOnly ? '드래그하여 회전 · 핀치로 확대' : '드래그하여 회전 · 스크롤로 확대';
}

/**
 * 주행 중 키보드 조작 안내.
 *
 * 자리를 따로 잡지 않고 계기판이 0을 가리킬 때 그 자리에 대신 띄웁니다. 세워 두는
 * 동안에는 속도 숫자가 알려 줄 것이 없고, 그 순간이 바로 어떻게 움직이는지 찾는
 * 순간입니다. 아래에 알약으로 한 줄 세우면 뷰어 세로를 36px 먹는데, 뷰어가 46dvh뿐인
 * 세로 배치에서는 그만큼이 차가 보일 자리입니다.
 *
 * 제동(스페이스)은 넣지 않습니다. 정지 중에 필요한 조작이 아니고, 계기판 폭(84px)에
 * 두 줄로 들어가는 길이를 넘깁니다 — 세 줄이 되면 조작 버튼보다 키가 커집니다.
 */
const DRIVING_IDLE_HINT = '방향키 또는 WASD';

export function ShowroomPage() {
  const {
    selectedOptions,
    requestedModelId,
    onModelSelect,
    onStudioSelect,
    onWeatherSelect,
    onPaintSelect,
    onWheelSelect,
    onInteriorSelect,
  } = useCarConfiguration();
  const { theme, setTheme } = useColorTheme();
  const {
    preferences: drivingPreferences,
    course: drivingCourse,
    timeOfDay,
    camera: drivingCamera,
    challenge: drivingChallenge,
    onCourseSelect: selectDrivingCourse,
    onTimeOfDaySelect,
    onCameraSelect,
    onWeatherModeToggle,
    onChallengeSelect,
    onRegenModeSelect,
    onAssistToggle,
    onTuningSelect,
    onTuningReset,
  } = useDrivingPreferences();
  const { quality: graphicsQuality, onQualitySelect: onGraphicsQualitySelect } =
    useGraphicsQuality();
  const {
    getLapBest,
    getChallengeBest,
    saveLapResult,
    saveChallengeResult,
  } = useDrivingRecords();
  const [requestedViewMode, setRequestedViewMode] = useState<ViewMode>(VIEW_MODES.EXTERIOR);
  const [stageMode, setStageMode] = useState<StageMode>(STAGE_MODES.VIEWING);
  const [panelCollapseRequested, setPanelCollapseRequested] = useState(false);
  const isStackedLayout = useStackedLayout();
  const isTouchOnly = useTouchOnly();

  const { model } = selectedOptions;
  const interiorAvailable = hasInteriorView(model);
  const isDriving = stageMode === STAGE_MODES.DRIVING;
  /** 전기차 출력 게이지의 만눈금. 차를 바꿀 때만 다시 계산합니다. */
  const peakPowerKw = useMemo(
    () =>
      catalogPeakPowerKw({
        bounds: model.bounds,
        powertrain: model.powertrain,
        topSpeedKmh: model.topSpeedKmh,
        zeroTo100KmhSeconds: model.zeroTo100KmhSeconds,
        kerbWeightKg: model.kerbWeightKg,
        electricEnergy: model.electricEnergy,
      }),
    [model]
  );

  /**
   * 주행 운동 상태.
   *
   * React state가 아니라 ref인 이유: 3D 루프가 매 프레임 갱신하는 값이라 state로 두면
   * 프레임마다 이 페이지와 옵션 패널까지 다시 렌더됩니다. 여기서는 그릇만 만들어
   * 3D(쓰기)와 HUD(읽기)에 같은 ref를 넘깁니다. 자세한 이유는 CarDriver 주석에 있습니다.
   */
  const drivingStateRef = useRef<VehicleState>(createVehicleState(drivingCourse.spawn));
  const drivingSessionRef = useRef<DrivingSessionState>(
    createDrivingSession(
      drivingCourse.id,
      0,
      getLapBest(model.id, drivingCourse.id)
    )
  );
  const drivingChallengeRef = useRef<DrivingChallengeState>(
    createDrivingChallengeState(
      drivingPreferences.challengeId,
      0,
      getChallengeBest(model.id, drivingPreferences.challengeId)
    )
  );
  const frameStatsRef = useRef<FrameStatsValue>(createFrameStats());
  const drivingTelemetryRef = useRef<DrivingTelemetryState>(
    createDrivingTelemetryState(
      model.id,
      drivingCourse.id,
      drivingPreferences.challengeId,
      drivingStateRef.current.spawnCount
    )
  );
  const { inputRef, actions, press, release, gamepadName, gamepadIndexRef } =
    useDrivingInput(isDriving);
  useGamepadHaptics(isDriving, gamepadIndexRef, drivingStateRef);

  /**
   * "출발선으로" 버튼.
   *
   * 코스를 벗어나면 자동으로 출발선으로 돌아오지만, 코스 안에서 방향을 잃었을 때
   * 처음부터 다시 시작할 방법도 있어야 합니다. 반경 1.2km 맵을 직접 돌아오는 것보다
   * 빠릅니다.
   *
   * 이탈 리스폰과 같은 함수를 쓰는 이유는 카메라입니다. respawnVehicle이 리스폰 횟수를
   * 올려 주고, CarDriver가 그 변화를 보고 카메라를 순간이동시킵니다. 직접 상태를 새로
   * 만들면 횟수가 0으로 돌아가서 카메라가 코스를 가로질러 미끄러집니다.
   */
  const handleCourseSelect = useCallback(
    (courseId: string) => {
      const nextCourse = drivingCourseOptions.find((course) => course.id === courseId);
      if (nextCourse === undefined || nextCourse.id === drivingCourse.id) {
        return;
      }
      selectDrivingCourse(courseId);
      drivingStateRef.current = respawnVehicle(
        drivingStateRef.current,
        SPAWN_REASONS.COURSE_CHANGE,
        nextCourse.spawn
      );
      drivingSessionRef.current = createDrivingSession(
        nextCourse.id,
        drivingStateRef.current.spawnCount,
        getLapBest(model.id, nextCourse.id)
      );
      drivingChallengeRef.current = createDrivingChallengeState(
        drivingPreferences.challengeId,
        drivingStateRef.current.spawnCount,
        getChallengeBest(model.id, drivingPreferences.challengeId)
      );
      drivingTelemetryRef.current = createDrivingTelemetryState(
        model.id,
        nextCourse.id,
        drivingPreferences.challengeId,
        drivingStateRef.current.spawnCount
      );
    },
    [
      drivingCourse.id,
      drivingPreferences.challengeId,
      getChallengeBest,
      getLapBest,
      model.id,
      selectDrivingCourse,
    ]
  );

  /**
   * 챌린지 선택.
   *
   * 코스를 요구하는 종목은 그 코스로 함께 전환합니다. 선택만 바꾸면 측정이 시작되지 않아
   * 사용자는 기능이 고장 난 것으로 읽습니다.
   */
  const handleChallengeSelect = useCallback(
    (challengeId: DrivingChallengeId) => {
      onChallengeSelect(challengeId);
      const option = drivingChallengeOptions.find((item) => item.id === challengeId);
      const requiredCourseId = option?.requiredCourseId;
      const needsCourseChange =
        requiredCourseId !== undefined && requiredCourseId !== drivingCourse.id;
      const nextCourse = needsCourseChange
        ? drivingCourseOptions.find((course) => course.id === requiredCourseId)
        : undefined;

      if (nextCourse !== undefined) {
        selectDrivingCourse(nextCourse.id);
      }
      const spawn = nextCourse?.spawn ?? drivingCourse.spawn;
      drivingStateRef.current = respawnVehicle(
        drivingStateRef.current,
        nextCourse === undefined ? SPAWN_REASONS.RECENTER : SPAWN_REASONS.COURSE_CHANGE,
        spawn
      );
      drivingSessionRef.current = createDrivingSession(
        nextCourse?.id ?? drivingCourse.id,
        drivingStateRef.current.spawnCount,
        getLapBest(model.id, nextCourse?.id ?? drivingCourse.id)
      );
      drivingChallengeRef.current = createDrivingChallengeState(
        challengeId,
        drivingStateRef.current.spawnCount,
        getChallengeBest(model.id, challengeId)
      );
      drivingTelemetryRef.current = createDrivingTelemetryState(
        model.id,
        nextCourse?.id ?? drivingCourse.id,
        challengeId,
        drivingStateRef.current.spawnCount
      );
    },
    [drivingCourse, getChallengeBest, getLapBest, model.id, onChallengeSelect, selectDrivingCourse]
  );

  const handleRecenter = useCallback(() => {
    drivingStateRef.current = respawnVehicle(
      drivingStateRef.current,
      SPAWN_REASONS.RECENTER,
      drivingCourse.spawn
    );
    drivingSessionRef.current = createDrivingSession(
      drivingCourse.id,
      drivingStateRef.current.spawnCount,
      getLapBest(model.id, drivingCourse.id)
    );
    drivingChallengeRef.current = createDrivingChallengeState(
      drivingPreferences.challengeId,
      drivingStateRef.current.spawnCount,
      getChallengeBest(model.id, drivingPreferences.challengeId)
    );
  }, [
    drivingCourse,
    drivingPreferences.challengeId,
    getChallengeBest,
    getLapBest,
    model.id,
  ]);

  /**
   * 완료 이벤트만 React/localStorage로 올립니다. 물리 프레임마다 저장하지 않으므로
   * Canvas와 옵션 패널은 주행 중 다시 렌더되지 않습니다.
   */
  const handleLapCompleted = useCallback(
    (courseId: string, resultSeconds: number) => {
      saveLapResult(model.id, courseId, resultSeconds);
    },
    [model.id, saveLapResult]
  );
  const handleChallengeCompleted = useCallback(
    (challengeId: DrivingChallengeId, result: number) => {
      saveChallengeResult(model.id, challengeId, result);
    },
    [model.id, saveChallengeResult]
  );

  /**
   * 무대 모드 전환.
   *
   * 주행으로 들어갈 때 배경을 서킷으로 갈아 끼웁니다. 이유는 data/studioOptions의
   * DRIVING_STUDIO_ID 주석에 있습니다.
   *
   * 파생값(viewMode처럼 렌더 중에 덮어쓰기)으로 하지 않은 이유: 배경 스와치는 주행
   * 중에도 열려 있습니다. 덮어쓰면 사용자가 스와치를 눌러도 화면이 서킷에 붙어 있어서
   * 조작이 먹지 않는 것처럼 보입니다. 실제 선택으로 반영하면 스와치도 서킷을 가리키고
   * 거기서 다시 고를 수 있습니다.
   *
   * 같은 모드를 다시 누르면 아무것도 하지 않습니다. 세그먼트는 이미 선택된 쪽을 눌러도
   * onClick이 오는데, 그때마다 배경을 되돌리면 몰면서 고른 배경이 지워집니다.
   */
  const handleStageModeChange = useCallback(
    (mode: StageMode) => {
      if (mode === stageMode) {
        return;
      }
      setStageMode(mode);
      if (mode === STAGE_MODES.DRIVING) {
        onStudioSelect(DRIVING_STUDIO_ID);
        // 모델을 바꾸고 다시 주행해도 이전 모델의 세션/배터리가 섞이지 않게 새 시도를 엽니다.
        handleRecenter();
      }
      // 주행 모드를 넘나들 때 이전 차/활동의 완료 그래프를 새 HUD에 재사용하지 않습니다.
      drivingTelemetryRef.current = createDrivingTelemetryState(
        model.id,
        drivingCourse.id,
        drivingPreferences.challengeId,
        drivingStateRef.current.spawnCount
      );
    },
    [
      stageMode,
      drivingCourse.id,
      drivingPreferences.challengeId,
      handleRecenter,
      model.id,
      onStudioSelect,
    ]
  );

  const handlePanelToggle = useCallback(() => {
    setPanelCollapseRequested((collapsed) => !collapsed);
  }, []);

  /**
   * 패널을 접은 상태인지.
   *
   * 좌우로 나뉜 배치에서만 성립합니다. 세로로 쌓이면 패널은 뷰어 **아래**에 있어서
   * 접어도 뷰어가 넓어지지 않고, 그냥 옵션이 사라질 뿐입니다.
   *
   * 사용자가 고른 값(panelCollapseRequested)을 지우지 않고 파생값으로 덮는 이유는
   * 창 크기 변화입니다. 접은 채로 창을 세로로 좁히면 접기 버튼도 사라지는데(그 배치에는
   * 없는 기능입니다) 상태만 남아 있으면 패널을 되살릴 방법이 없습니다. 파생값으로 두면
   * 좁힐 때 패널이 돌아오고, 다시 넓히면 접어 둔 상태가 되살아납니다.
   */
  const isPanelCollapsed = panelCollapseRequested && !isStackedLayout;

  /**
   * 실내를 보던 중에 외관 전용 모델로 바꾸면 갈 곳 없는 시점이 됩니다.
   * setState로 되돌리지 않고 파생값으로 처리하는 이유는, effect로 고치면
   * 한 프레임 동안 잘못된 카메라로 렌더된 뒤에 교정되기 때문입니다.
   * 사용자가 고른 값(requestedViewMode)은 그대로 두어서, 실내가 있는 모델로
   * 되돌아오면 다시 실내 뷰가 됩니다.
   *
   * 주행 모드도 같은 이유로 여기서 외관으로 덮습니다. 운전석에 앉으면 카메라가 차와
   * 함께 움직여서 주행이 보이지 않습니다. 보기 모드로 나오면 고른 시점이 되살아납니다.
   */
  const viewMode = isDriving || !interiorAvailable ? VIEW_MODES.EXTERIOR : requestedViewMode;

  /**
   * 손가락으로만 조작하는 기기에서는 키보드 안내를 넘기지 않습니다. 방향키도 스페이스도
   * 없는 화면에서는 읽어도 쓸 데가 없고, 그쪽은 계기판이 "정지"로 남습니다
   * (DrivingSpeedReadout의 기본값).
   */
  const idleLabel = isTouchOnly ? undefined : DRIVING_IDLE_HINT;

  return (
    <div
      className={buildClassName(
        buildClassName('showroom-page', 'showroom-page--panel-collapsed', isPanelCollapsed),
        'showroom-page--driving',
        isDriving
      )}
    >
      <header className="showroom-header">
        <p className="showroom-header__brand">TOWNCAR SHOWROOM</p>
        <h1 className="showroom-header__title">{model.name}</h1>
        <p className="showroom-header__subtitle">{formatModelSubtitle(model)}</p>
      </header>

      <main className="showroom-viewer" aria-label="3D 차량 뷰어">
        {/*
          패널 접기.

          뷰어 위 오른쪽 끝, 패널과 닿는 쪽에 둡니다. 접는 대상이 그 방향에 있어서
          버튼이 어디를 가리키는지 화살표를 읽지 않아도 짚힙니다.

          접었을 때와 펼쳤을 때가 같은 버튼입니다. 펼친 상태에서는 패널 안에 두는 것이
          자연스러워 보이지만, 접으면 패널이 사라지므로 버튼이 함께 사라집니다. 두 곳에
          하나씩 두면 같은 일을 하는 버튼이 둘이 되어 스크린 리더에서 구분되지 않습니다.

          세로로 쌓이는 배치에서는 렌더하지 않습니다. 그 배치의 패널은 뷰어 아래에 있어서
          접어도 뷰어가 넓어지지 않습니다(isPanelCollapsed 주석 참고).
        */}
        {isStackedLayout ? null : (
          <button
            type="button"
            className="panel-toggle"
            // 열림/닫힘을 상태로 알립니다. 글자만 바꾸면 버튼이 지금 어느 상태인지
            // 스크린 리더가 알려 줄 방법이 없습니다.
            aria-expanded={!isPanelCollapsed}
            onClick={handlePanelToggle}
          >
            {/* 방향만 나타내는 장식이라 읽지 않게 둡니다. */}
            <span className="panel-toggle__chevron" aria-hidden="true">
              {isPanelCollapsed ? '◀' : '▶'}
            </span>
            {isPanelCollapsed ? '옵션 펼치기' : '옵션 접기'}
          </button>
        )}

        <ViewerErrorBoundary>
          <ShowroomCanvas
            model={model}
            studio={selectedOptions.studio}
            selectedOptions={selectedOptions}
            viewMode={viewMode}
            stageMode={stageMode}
            drivingInputRef={inputRef}
            drivingStateRef={drivingStateRef}
            drivingSessionRef={drivingSessionRef}
            drivingChallengeRef={drivingChallengeRef}
            drivingTelemetryRef={drivingTelemetryRef}
            drivingCourse={drivingCourse}
            drivingAssists={drivingPreferences.assists}
            drivingRegenMode={drivingPreferences.regenMode}
            drivingTuning={drivingPreferences.tuning}
            drivingCamera={drivingCamera}
            drivingChallenge={drivingChallenge}
            timeOfDay={timeOfDay}
            dynamicWeather={drivingPreferences.weatherMode === 'dynamic'}
            graphicsQuality={graphicsQuality}
            frameStatsRef={frameStatsRef}
            onLapCompleted={handleLapCompleted}
            onChallengeCompleted={handleChallengeCompleted}
          />
        </ViewerErrorBoundary>
        <ViewerLoadingOverlay />

        {isDriving ? (
          <DrivingHud
            vehicleStateRef={drivingStateRef}
            sessionRef={drivingSessionRef}
            challengeStateRef={drivingChallengeRef}
            telemetryStateRef={drivingTelemetryRef}
            frameStatsRef={frameStatsRef}
            powertrain={model.powertrain}
            peakPowerKw={peakPowerKw}
            regenLimitKw={model.electricEnergy?.regenLimitKw ?? peakPowerKw * 0.35}
            course={drivingCourse}
            challenge={drivingChallenge}
            assists={drivingPreferences.assists}
            gamepadName={gamepadName}
          />
        ) : null}

        {/*
          코스 이탈 안내는 footer가 아니라 뷰어 위쪽 중앙에 띄웁니다. 아래쪽은 조작
          패드가 차지하고 있고, 리스폰 직후에는 시선이 차(화면 중앙)에 있습니다.
        */}
        {isDriving ? <DrivingRunoffNotice stateRef={drivingStateRef} /> : null}

        {/*
          저작자 표기는 뷰어 밖에서 합니다.
          CC-BY는 표기를 "매체와 맥락에 맞는 합리적인 방식"으로 하면 되므로
          3D 화면 위에 항상 띄워 둘 필요는 없습니다. 다만 앱 어딘가에는
          반드시 있어야 하고, 없으면 네 에셋 전부 라이선스 위반입니다.
          컴포넌트는 AssetCredit.tsx에 그대로 있습니다.
        */}
        <div className="showroom-viewer__footer">
          {isDriving ? (
            <DrivingControls
              actions={actions}
              onPress={press}
              onRelease={release}
              stateRef={drivingStateRef}
              idleLabel={idleLabel}
            />
          ) : null}

          <div className="showroom-viewer__modes">
            {/*
              세로로 쌓이는 좁은 화면에서는 보기 모드를 뷰어 위로 올립니다.
              그 배치에서 패널은 접힌 화면 아래쪽에 있어서, 외관과 실내를 번갈아 보려면
              매번 스크롤을 내려 토글을 찾고 다시 올려서 결과를 봐야 합니다.

              패널에 두고 CSS로 옮기는 방법은 없습니다. 패널은 별도 그리드 영역이고
              overflow-y: auto라서 그 안의 요소는 뷰어 위로 빠져나오지 못합니다.
              그래서 렌더 위치 자체를 바꾸고, 두 곳에 동시에 두지 않습니다
              (같은 이름의 버튼이 둘이면 스크린 리더에서 어느 쪽인지 알 수 없습니다).

              주행 중에는 이 오버레이만 감춥니다. 패널 쪽(데스크톱)은 비활성 상태로
              남겨 두는데, 그쪽은 자리를 비우면 패널 전체가 덜컥거리기 때문입니다.
              여기는 반대로 주행 조작이 들어와 세로 공간이 빠듯하고, 뷰어가 가장 낮은
              세로 배치에서는 차가 보일 자리를 침범합니다.
            */}
            {isStackedLayout && !isDriving ? (
              <div className="showroom-viewer__view-mode">
                <ViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setRequestedViewMode}
                  interiorAvailable={interiorAvailable}
                />
              </div>
            ) : null}
            <StageModeToggle stageMode={stageMode} onStageModeChange={handleStageModeChange} />

            {/*
              "출발선으로"를 조작 패드가 아니라 이 줄에 둡니다.

              누르고 있는 조작(조향·페달)과 성격이 다릅니다 — 무대를 되돌리는 한 번짜리
              동작이고, 주행 중에 실수로 누르면 달리던 것이 초기화되므로 오히려 엄지에서
              멀어야 합니다. 조작 패드에서 빼면 그 바가 버튼 한 줄 높이로 얇아지고,
              여기는 세그먼트 옆에 폭이 남습니다.

              늘 활성입니다 — 지금 위치는 3D 루프가 ref에 들고 있어서 렌더 중에 읽어도
              최신값이 아니고, 비활성화하려면 그 값을 매 프레임 React로 끌어올려야 합니다.
              이미 출발선에 있을 때 눌러도 해가 없으므로 그 비용을 낼 이유가 없습니다.
            */}
            {isDriving ? (
              <button
                type="button"
                className="stage-recenter"
                // 보이는 글자를 포함하는 이름입니다(WCAG 2.5.3 Label in Name).
                aria-label="차량을 트랙 출발선으로"
                onClick={handleRecenter}
              >
                출발선으로
              </button>
            ) : null}
          </div>

          {/*
            주행 중에는 이 줄이 없습니다.

            시점이 고정이라 드래그·확대 안내는 틀린 설명이 되고, 남는 것은 키보드 조작
            안내뿐인데 그것은 계기판 자리로 들어갔습니다(위 idleLabel). 여기에도 두면
            같은 문구가 두 번 보이고 스크린 리더도 두 번 읽습니다.
          */}
          {isDriving ? null : (
            <p className="showroom-viewer__hint">{resolveViewerHint(viewMode, isTouchOnly)}</p>
          )}
        </div>
      </main>

      {/*
        접었을 때 CSS로 감추지 않고 렌더에서 빼는 이유:
          폭 0으로 접어 두면 화면에 없는 옵션이 Tab 순회에는 그대로 남습니다. 보이지
          않는 스와치들을 하나씩 지나야 다음 요소에 닿게 되고, 키보드 사용자에게는
          접기가 오히려 방해가 됩니다.

        대신 폭이 부드럽게 줄어드는 전환은 포기합니다. 접었다 펴면 패널 스크롤도 맨
        위로 돌아갑니다 — display를 건드리는 순간 스크롤 박스가 사라지므로, 폭 0으로
        감추는 쪽을 골라도 마찬가지입니다.
      */}
      {isPanelCollapsed ? null : (
        <aside className="showroom-panel" aria-label="옵션 선택">
          <OptionPanel
            theme={theme}
            onThemeChange={setTheme}
            viewMode={viewMode}
            onViewModeChange={setRequestedViewMode}
            showViewModeToggle={!isStackedLayout}
            specLocked={isDriving}
            selectedOptions={selectedOptions}
            requestedModelId={requestedModelId}
            onModelSelect={onModelSelect}
            onStudioSelect={onStudioSelect}
            onWeatherSelect={onWeatherSelect}
            drivingCourse={drivingCourse}
            timeOfDay={timeOfDay}
            drivingCamera={drivingCamera}
            weatherMode={drivingPreferences.weatherMode}
            drivingChallenge={drivingChallenge}
            drivingAssists={drivingPreferences.assists}
            regenMode={drivingPreferences.regenMode}
            vehicleTuning={drivingPreferences.tuning}
            graphicsQuality={graphicsQuality}
            onCourseSelect={handleCourseSelect}
            onTimeOfDaySelect={onTimeOfDaySelect}
            onCameraSelect={onCameraSelect}
            onWeatherModeToggle={onWeatherModeToggle}
            onChallengeSelect={handleChallengeSelect}
            onRegenModeSelect={onRegenModeSelect}
            onAssistToggle={onAssistToggle}
            onTuningSelect={onTuningSelect}
            onTuningReset={onTuningReset}
            onGraphicsQualitySelect={onGraphicsQualitySelect}
            onPaintSelect={onPaintSelect}
            onWheelSelect={onWheelSelect}
            onInteriorSelect={onInteriorSelect}
          />
        </aside>
      )}
    </div>
  );
}
